import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { userHasPermission } from '../services/permissions';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    const isAuth = this.authService.isAuthenticated();

    if (!isAuth) {
      localStorage.setItem('redirectUrl', state.url);
      this.router.navigate(['/login']);
      return false;
    }

    const user = this.authService.currentUserValue;
    if (!user) {
      this.authService.logout();
      this.router.navigate(['/login']);
      return false;
    }

    const requireAdmin = (route.data?.['requireAdmin'] as boolean) ?? false;
    if (requireAdmin && !this.authService.hasGlobalDataAccess()) {
      return this.handleInsufficientPermissions(state.url);
    }

    // Stricter than requireAdmin: also demands no entity selected (operating globally right
    // now), not just the global-access level. Use for pages showing unscoped, cross-entity data.
    const requireGlobalScope = (route.data?.['requireGlobalScope'] as boolean) ?? false;
    if (requireGlobalScope && !this.authService.hasGlobalDataScope()) {
      return this.handleInsufficientPermissions(state.url);
    }

    const requiredPermissions = (route.data?.['requiredPermissions'] as string[]) || [];
    const missingPermission = requiredPermissions.some(permission => {
      return !this.authService.hasPermission(permission);
    });

    if (missingPermission) {
      return this.handleInsufficientPermissions(state.url);
    }

    const strictPermissions = (route.data?.['strictPermissions'] as string[]) || [];
    if (strictPermissions.length > 0) {
      const missingStrict = strictPermissions.some(p => !userHasPermission(user.permissions, p));
      if (missingStrict) {
        return this.handleInsufficientPermissions(state.url);
      }
    }

    // Some products (Vision) need not just the permission but an active DCR that
    // actually has Vision access. Selecting "_TNC Data" (global scope) or a DCR
    // without Vision must not reach these pages — their views hang with no valid
    // DCR context. On a hard refresh the stored DCR list lacks the hasVisionAccess
    // flag until the entity re-sync completes, so we wait for it before deciding,
    // otherwise a legitimate Vision entity would be wrongly redirected to /home.
    const requireVisionDcr = (route.data?.['requireVisionDcr'] as boolean) ?? false;
    if (requireVisionDcr) {
      return this.authService.waitForEntitySync().pipe(
        map(() => {
          if (!this.authService.currentDcrHasVisionAccess()) {
            return this.handleInsufficientPermissions(state.url);
          }
          return true;
        })
      );
    }

    return true;
  }

  // I `requiredPermissions` delle pagine stanno sulle rotte figlie (es. /reach/destinations),
  // mentre `canActivate` sul padre vede solo il data del padre: senza questo hook un utente
  // senza permesso apriva la pagina digitando l'URL. I padri sono componentless, quindi la
  // snapshot del figlio eredita anche il data del padre (es. requireVisionDcr).
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    return this.canActivate(childRoute, state);
  }

  private handleInsufficientPermissions(targetUrl: string): boolean {
    // /home is reachable by any authenticated user, so it's a safe universal
    // fallback: a missing permission on one page must not log the user out.
    if (targetUrl !== '/home') {
      this.router.navigate(['/home']);
    } else {
      this.authService.logout();
      this.router.navigate(['/login']);
    }
    return false;
  }
}
